import React, { useEffect, useState } from 'react';
import { __ } from './translations';
import { Calendar, Clock, MapPin, ExternalLink, Loader, AlertCircle } from 'lucide-react';

interface Event {
    id: number;
    title: string;
    url: string;
    start_date: string;
    end_date: string;
    venue?: {
        venue: string;
        address: string;
    };
    description?: string;
}

const Events: React.FC<{ restURL: string; nonce: string }> = ({ restURL, nonce }) => {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${restURL}tribe/events/v1/events`, {
                    headers: {
                        'X-WP-Nonce': nonce
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch events');
                }

                const data = await response.json();
                setEvents(data.events.slice(0, 5)); // Limit to 5 latest events
            } catch (error) {
                console.error('Error fetching events:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch events');
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, [restURL, nonce]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader className="h-8 w-8 text-blue-500 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!events.length) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p>{__('No upcoming events found.')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-gray-500" />
                        {__('Latest Events')}
                    </h2>
                </div>
                <div className="divide-y divide-gray-200">
                    {events.map(event => (
                        <article
                            key={event.id}
                            className="p-6 hover:bg-gray-50 transition-colors"
                        >
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                <a
                                    href={event.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-blue-600 hover:underline flex items-center gap-2"
                                >
                                    {event.title}
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                            </h3>

                            <div className="space-y-2 text-sm text-gray-600">
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-gray-400" />
                                    <time dateTime={event.start_date}>
                                        {new Date(event.start_date).toLocaleDateString(undefined, {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        })}
                                    </time>
                                    <span className="text-gray-400">•</span>
                                    <time dateTime={event.start_date}>
                                        {new Date(event.start_date).toLocaleTimeString(undefined, {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </time>
                                </div>

                                {event.venue && (
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4 text-gray-400" />
                                        <span>
                                            {event.venue.venue}
                                            {event.venue.address && ` - ${event.venue.address}`}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {event.description && (
                                <div className="mt-3 text-sm text-gray-600 line-clamp-2">
                                    <div dangerouslySetInnerHTML={{
                                        __html: event.description.substring(0, 150) + '...'
                                    }} />
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Events;